/* eslint-disable jsx-a11y/anchor-is-valid */
import { Box } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { IS_SA } from '../../../config/appConfig';
import { OTPTypes } from '../../../constants/otpTypes';
import { FormHelper } from '../../../helpers/formHelper';
import { maskPhone } from '../../../helpers/phoneHelper';
import { getUser, loginUsingToken, saveAuthTokens, serverNowFrom } from '../../../services/authService';
import { OtpService } from '../../../services/otpService';
import { FsDialog, FsDialogHeader, FsDialogBody } from '../dialog';
import { FsButton } from '../button';
import { Toaster } from '../common/toaster';
import { InternationalPhone } from '../../../shared/components/formUtilities/useInternationalPhone';
import { TextWrapper } from '../../../shared/components/formUtilities/useTextWrapper';
import { OTPAuthenticationSchema } from '../../../shared/validations/schema';

const CheckIconSvg = () => (
	<svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff"
		strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
		<polyline points="20 6 9 17 4 12" />
	</svg>
);

const ArrowIconSvg = () => (
	<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
		strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
		<line x1="5" y1="12" x2="19" y2="12" />
		<polyline points="12 5 19 12 12 19" />
	</svg>
);

export const OTPAuthenticationDialog = ({
	state,
	onClose = (e) => { },
	isUnAuth = false,
	roles = [],
	userEmail = '',
	corrolationId = '',
	type = '',
}) => {
	const otpService = new OtpService();
	const [OTP, setOTP] = useState('');
	const [loading, setLoading] = useState(false);
	const [counter, setCounter] = useState(0);
	const [mobileNumber, setMobileNumber] = useState(null);
	const [showChangeMobile, setShowChangeMobile] = useState(false);
	const { t } = useTranslation();
	const user = getUser();
	const form = useFormik({
		initialValues: {
			contactNumber: '',
		},
		validationSchema: OTPAuthenticationSchema,
		...FormHelper.formikProps(),
	});

	useEffect(() => {
		onSendOTP();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state]);

	useEffect(() => {
		if (counter <= 0) return;
		const id = setTimeout(() => setCounter((c) => c - 1), 1000);
		return () => clearTimeout(id);
	}, [counter]);

	const onVerify = (form) => {
		setLoading(true);
		if (!isUnAuth) {
			otpService
				.verifyOTP(form)
				.then((resp) => {
					setLoading(false);
					afterSuccessOTP(resp.data, serverNowFrom(resp));
					Toaster({ msg: t('otpVerficationSuccess'), isError: false });
				})
				.catch((err) => { setLoading(false); });
		} else {
			form['UserEmail'] = userEmail;
			otpService
				.unAuthVerifyOTP(form)
				.then((resp) => {
					afterSuccessOTP(resp.data);
					setLoading(false);
				})
				.catch((err) => { setLoading(false); });
		}
	};

	const isApproved = () => {
		return !isUnAuth ? user?.isApproved : true;
	};

	const sendOTP = () => {
		if (isUnAuth) {
			return otpService.unAuthSendOTP(corrolationId, type, userEmail);
		} else {
			return otpService.sendOTP(corrolationId, type);
		}
	};

	const onSendOTP = () => {
		sendOTP()
			.then((resp) => {
				// Only build the display string when the API actually returned a
				// phone; otherwise leave it empty so the subtitle can fall back to
				// the no-number copy instead of rendering "undefined undefined".
				const code = resp?.data?.phoneNumber?.code;
				const number = resp?.data?.phoneNumber?.number;
				const phone = code && number ? `${code} ${number}` : '';
				setMobileNumber(phone);
				form.setFieldValue('contactNumber', phone);
				setCounter(180);
			})
			.catch((err) => { });
	};

	const onResend = () => {
		onSendOTP();
	};

	const onSubmit = () => {
		if (OTP) {
			const form = {
				code: OTP,
				type: type,
			};
			onVerify(form);
		}
	};

	const afterSuccessOTP = (data, serverNowMs) => {
		if (type === OTPTypes.Authentication) {
			if (typeof data === 'string') {
				loginUsingToken(data);
			} else {
				saveAuthTokens(data, serverNowMs);
			}
		}
		onClose(true);
	};

	const onUpdateMobile = () => {
		let phoneNumber = form.values.contactNumber;
		if (phoneNumber) {
			let phone = {
				code: phoneNumber?.dialCode,
				number: phoneNumber?.number,
			};
			otpService
				.updateMobileNumber(phone)
				.then((resp) => {
					setCounter(0);
					setShowChangeMobile(false);
					onResend();
				})
				.catch((err) => { });
		}
	};

	const onBackToOTP = () => {
		setShowChangeMobile(false);
		form.resetForm();
	};

	const otpComplete = OTP.length === 4;
	const countdownLabel = `${Math.floor(counter / 60)}:${String(counter % 60).padStart(2, '0')}`;

	// Render the instruction line with the masked number emphasised, e.g.
	// "Enter the 4-digit code … your Absher registered number 96658*****70."
	const renderSubtitle = () => {
		const masked = maskPhone(mobileNumber);
		// Show the international "+" prefix in front of the masked number, e.g.
		// "+96658*****70". maskPhone strips it along with other non-digits.
		const display = masked ? `+${masked}` : masked;
		// When no number is available (e.g. the transaction OTP send response
		// doesn't include one), use copy that omits the number entirely so we
		// don't render a dangling "... registered number ." with a trailing dot.
		if (!display) {
			return <p className="vp-subtitle">{t('otpTextNoPhone')}</p>;
		}
		const text = t('otpText', { phone: display });
		// Only split out the number when it actually appears in the copy; a
		// translation missing the {{phone}} placeholder falls back to plain text.
		if (!text.includes(display)) {
			return <p className="vp-subtitle">{text}</p>;
		}
		const [before, after] = text.split(display);
		return (
			<p className="vp-subtitle">
				{before}
				<span className="vp-phone">{display}</span>
				{after}
			</p>
		);
	};

	const getPrimaryView = () => (
		<>
			<div>{renderSubtitle()}</div>

			<div>
				<div className="vp-otp-label-row">
					<span className="vp-otp-label">{t('verificationCode')}</span>
					<span className={`vp-expires ${counter === 0 ? 'expired' : ''}`}>
						{t('expiresIn')} <b>{countdownLabel}</b>
					</span>
				</div>
				<OtpInput
					containerStyle="vp-otp otp-container"
					value={OTP}
					onChange={setOTP}
					shouldAutoFocus
					// KSA codes are always numeric (the API generates digits only), so
					// lock the field down there. "tel" is the library's documented
					// numeric mode: it rejects non-digit typing and pasting, and renders
					// inputMode="numeric" so mobile opens the numeric keypad. "number"
					// would restrict entry too, but adds spin buttons. Other regions keep
					// the flexible alphanumeric box.
					inputType={IS_SA ? 'tel' : 'text'}
					numInputs={4}
					renderInput={(props, index) => (
						<input
							{...props}
							autoComplete="one-time-code"
							autoCapitalize="off"
							autoCorrect="off"
							spellCheck={false}
							className={`vp-otp-input ${props.value ? 'filled' : ''}`}
						/>
					)}
				/>
			</div>

			{counter > 0 && (
				<div className="flex justify-center">
					<div className="vp-toast">
						<span className="vp-toast-check"><CheckIconSvg /></span>
						{t('otpSentSuccessfully')}
					</div>
				</div>
			)}

			<button
				type="button"
				className="vp-cta"
				aria-label={t('verify')}
				aria-busy={loading}
				disabled={!otpComplete || loading}
				onClick={() => onSubmit()}>
				{loading ? (
					<span className="vp-spinner" />
				) : (
					<>
						{t('verify')}
						<ArrowIconSvg />
					</>
				)}
			</button>

			<div className="vp-resend">
				{t('didntGetACode')}{' '}
				<button
					type="button"
					className="vp-link"
					disabled={counter !== 0}
					onClick={() => onResend()}>
					{t('resend')}
				</button>
			</div>
		</>
	);

	const getChangeMobileView = () => (
		<>
			<div>
				<TextWrapper
					classList="!info-text"
					text={t('pleaseEnterYourMobileNumber')}></TextWrapper>
			</div>
			<FormikProvider value={form}>
				<InternationalPhone
					phoneWithCode={mobileNumber}
					name={'contactNumber'}
					key={'contactNumber'}
					label={'contactNumber'}></InternationalPhone>
			</FormikProvider>
			<Box className="grid grid-cols-2 gap-2">
				<FsButton
					fullWidth
					loading={loading}
					variant="outline"
					type="button"
					onClick={() => onBackToOTP()}>{t('back')}</FsButton>
				<FsButton
					fullWidth
					loading={loading}
					disabled={!form.values.contactNumber}
					type="button"
					onClick={() => onUpdateMobile()}>{t('update')}</FsButton>
			</Box>
		</>
	);

	return (
		<FsDialog
			preventOutsideClick
			maxWidth="xs"
			width={'440px'}
			state={state}
			onClose={() => {
				onClose(false);
			}}>
			<FsDialogHeader
				title={showChangeMobile ? t('changeMobileNumber') : t('verifyYourAccount')}
				onClose={() => onClose(false)}
			/>
			<FsDialogBody>
				{!showChangeMobile ? getPrimaryView() : getChangeMobileView()}
			</FsDialogBody>
		</FsDialog>
	);
};
